/**
 * client/src/sync/room.ts — the co-read socket, the shared clock, and the three mechanisms that stop
 * a room from fighting its own members.
 *
 * design.md §7.1–§7.6. Forked from movies.karti.ai `client/src/lib/room.ts`, which is the reference
 * implementation and is quoted throughout. **The rewrite surface is exactly** (§7): the position
 * type, the drift policy, `Source` → book, `setSource`'s destructiveness, and annotation anchoring.
 * Everything else in that file was ported deliberately rather than re-derived, and the places where
 * it was ported *unchanged* say so, because "we thought about this and kept it" and "we never looked"
 * are indistinguishable in a diff.
 *
 * ── What lives here and what does not ───────────────────────────────────────
 * This file is the **transport and the arithmetic**: the socket, the clock offset estimator, the echo
 * suppression windows, the presence throttle. It imports no React and touches no DOM, so
 * `test/room-client.test.ts` drives every one of them under bare `node --test` with a fake socket and
 * a number for a clock — the same shape `AudioEngine` and `CueSync` already use in this repo, and for
 * the same reason: every bug worth catching here is about *when* something happens relative to a
 * clock, and a rendered component puts a real timer between the assertion and the thing asserted.
 *
 * `useRoomSync.ts` is the React half and the drift policy. `Room.tsx` is the screen.
 *
 * ── The one idea, applied to sync ───────────────────────────────────────────
 * **A room shares a sentence, not a page and not a second.** `BookPos.sid` is the truth, `ord` is
 * what "3 pages ahead" is computed from, and `positionSec` exists so movies' clock and drift loop
 * port verbatim. LAW 3 governs every use of the seconds: a client resolves an in-page offset from
 * ITS OWN cue list, by `sid` + `frac`, and never by reconstructing a position from a millisecond that
 * arrived over the wire. See `useRoomSync.ts`'s `roomTargetMs()`, which is the only place in the
 * client where a room clock and a local audio clock are compared, and which subtracts an *interval*
 * rather than two axis positions for exactly that reason.
 */

import type { BookPos, PlaybackMode } from "../../../shared/position.ts";
import { deviceId } from "../lib/id.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * The wire (§7.2) — imported, never restated
 *
 * `shared/protocol.ts` is owned by the server half of P5 and is the single definition of this wire.
 * Re-exported here so the six client files that speak it import from one module and a swap of the
 * underlying source is one edit — but **every name below resolves to `shared/protocol.ts`**, so a
 * field the server renames is a type error in this client rather than an `undefined` at runtime.
 * That is LAW 5's argument applied to the socket, and it is not theoretical: this file briefly
 * carried a provisional local copy written from §7.2's prose while `shared/protocol.ts` was being
 * built in a sibling session, and it diverged on the one thing that would not have shown up in
 * review — the copy had `ControlMode = "everyone" | "hosts" | "owner"` where the real one is
 * `"everyone" | "host"`. Every string comparison against it would have been false, so in a
 * host-controlled room the play button would have been greyed out for **everybody, including the
 * host**, with nothing anywhere to say why. Two definitions of one wire, for about an hour.
 * ══════════════════════════════════════════════════════════════════════════ */

export type {
  ChatMessage,
  ClientMsg,
  ControlAction,
  ControlMode,
  Member,
  NotReady,
  Playback,
  BlockedBy,
  RoomListing,
  RoomMember,
  RoomSettings,
  RoomSnapshot,
  ServerMsg,
} from "../../../shared/protocol.ts";

export {
  BLOCK_DEADLINE_MS,
  GESTURE_WINDOW_MS,
  MAX_CHAT_TEXT,
  MAX_MEMBER_NAME,
  RESUME_IDLE_MS,
  canAdmin,
  canControl,
  isHost,
  isMember,
} from "../../../shared/protocol.ts";

/**
 * §2.4's rule applied to the sync loop: **one constant, two consumers.**
 *
 * Every number the correction loop runs on is enforced by the server as well, and two copies of "how
 * far apart is too far apart" is how a client that thinks it is in sync gets re-seeked forever. They
 * are re-exported rather than re-declared so `useRoomSync.ts` and the test import them from the same
 * place the server does.
 */
export {
  AT_MIN_GAP_MS,
  BEST_RTT_CAP_MS,
  BEST_RTT_DECAY_PERIOD_MS,
  BEST_RTT_DECAY_RATE,
  DRIFT_PAUSED,
  DRIFT_PLAYING,
  PING_INTERVAL_MS,
  READ_TURN_MS,
  RECONNECT_BACKOFF_MS,
  RTT_REFRESH_MS,
  SUPPRESS_LOAD_MS,
  SUPPRESS_SEEK_MS,
  TICK_MS,
} from "../../../shared/protocol.ts";

import {
  AT_MIN_GAP_MS,
  BEST_RTT_CAP_MS,
  BEST_RTT_DECAY_PERIOD_MS,
  BEST_RTT_DECAY_RATE,
  PING_INTERVAL_MS,
  RECONNECT_BACKOFF_MS,
  RTT_REFRESH_MS,
  canControl as protocolCanControl,
} from "../../../shared/protocol.ts";
import type { ClientMsg, RoomSnapshot, RoomMember, ServerMsg } from "../../../shared/protocol.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * The clock
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Estimate `serverTime - clientTime` from ping/pong. **Lowest RTT wins**, with the two amendments
 * §7.1 names: the `< 250 ms` refresh escape (movies') and the decay on `bestRtt` (this repo's).
 *
 * Every number in and out is a parameter — there is no `Date.now()` in this class — so the decay is
 * testable in microseconds instead of minutes.
 */
export class ClockOffset {
  private best = Infinity;
  private off = 0;
  /** When `best` was last *improved*. The decay is measured from here, not from the last sample. */
  private bestAt = 0;
  private seeded = false;

  /**
   * Seed from a snapshot's `serverTime` before any pong lands.
   *
   * Ignores one-way latency entirely, which is why it only ever applies while `best` is `Infinity`:
   * an unmeasured offset is better than a zero one for the ~10 ms before the first pong, and worse
   * than a measured one forever after. movies gates this the same way.
   */
  seed(serverTime: number, now: number): void {
    if (this.best === Infinity) {
      this.off = serverTime - now;
      this.seeded = true;
    }
  }

  /**
   * A `pong` landed. `clientTime` is the stamp we sent; `now` is the local clock on arrival.
   *
   * Returns true when the sample was accepted, which the test asserts on rather than reaching into
   * the private fields.
   */
  sample(clientTime: number, serverTime: number, now: number): boolean {
    const rtt = now - clientTime;
    if (!Number.isFinite(rtt) || rtt < 0 || !Number.isFinite(serverTime)) return false;
    this.relax(now);

    const improves = rtt < this.best;
    if (!improves && rtt >= RTT_REFRESH_MS) return false;

    if (improves) {
      this.best = rtt;
      // Only an IMPROVEMENT restarts the decay clock. A sample admitted through the `< 250 ms`
      // escape leaves `best` untouched, so the record it represents is still ageing and must keep
      // ageing — resetting here would pin a stale bound in place for as long as the link stayed
      // merely-good, which is the exact wart this decay exists to remove.
      this.bestAt = now;
    }
    // The offset is refreshed on every accepted sample, improving or not. That is movies' escape and
    // it is what makes a slowly-drifting clock trackable at all.
    this.off = serverTime + rtt / 2 - now;
    this.seeded = true;
    return true;
  }

  /** `serverTime - clientTime`, in milliseconds. Zero until something has seeded it. */
  offset(): number {
    return this.off;
  }

  /** The current best-RTT bound, after decay. `Infinity` before the first pong. */
  bestRtt(now: number): number {
    this.relax(now);
    return this.best;
  }

  get measured(): boolean {
    return this.seeded;
  }

  /** The server's clock, as we believe it to be. */
  serverNow(now: number): number {
    return now + this.off;
  }

  private relax(now: number): void {
    if (this.best === Infinity) return;
    const elapsed = now - this.bestAt;
    if (!(elapsed >= BEST_RTT_DECAY_PERIOD_MS)) return;
    const periods = Math.floor(elapsed / BEST_RTT_DECAY_PERIOD_MS);
    this.bestAt += periods * BEST_RTT_DECAY_PERIOD_MS;
    this.best = Math.min(BEST_RTT_CAP_MS, this.best * (1 + BEST_RTT_DECAY_RATE) ** periods);
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * Echo suppression
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * A window during which media events are **the room's own doing** and must not be re-sent as fresh
 * intents.
 *
 * §7.4. Every programmatic operation opens one: applying a remote `play` makes the element fire
 * `play`, applying a remote seek makes it fire `seeked`, and a page load fires `pause`, `emptied`,
 * `loadedmetadata`, `seeked` and `play` in a bundle. Without this, one member's tap becomes a
 * broadcast, which becomes an apply on the other members, which becomes five more broadcasts.
 *
 * **A *user* driving the player IS a fresh intent**, and that is the whole reason this is a time
 * window and not a boolean flag set around the call: the events arrive one or more turns of the
 * event loop later, so there is no synchronous scope that contains them.
 */
export class Suppressor {
  private until = 0;

  /** Monotonic-max. See `SUPPRESS_LOAD_MS` for why an assignment here is a bug. */
  open(ms: number, now: number): void {
    if (!Number.isFinite(ms) || ms <= 0) return;
    this.until = Math.max(this.until, now + ms);
  }

  active(now: number): boolean {
    return now < this.until;
  }

  remaining(now: number): number {
    return Math.max(0, this.until - now);
  }

  /** Used when the socket drops: a suppression window outlives its reason. */
  clear(): void {
    this.until = 0;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * Presence throttle
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * `{t:"at"}` on sentence change, at most 2/s (§7.6), **leading edge with a trailing flush**.
 *
 * Leading edge because the first move must be instant — a room where the other person's dot lags
 * half a second behind their page turn reads as broken. Trailing flush because dropping the *last*
 * update of a burst is how a member's dot ends up parked one sentence behind where they actually
 * stopped, permanently, until they move again.
 *
 * ⚠️ §7.1: **nothing writes to SQLite inside the 250 ms tick.** `better-sqlite3` is synchronous and
 * this message lands on the same event loop that is streaming audio; the server's 800 ms debounce is
 * the other half of this contract and neither half is sufficient alone.
 */
export class AtThrottle {
  private lastSentAt = -Infinity;
  private pending: { pos: BookPos; following: boolean; visibleEndSid?: string } | null = null;
  /**
   * The last value **offered**, sent or not — not the pending buffer.
   *
   * ⚠️ Comparing against `pending` looks equivalent and is not: `drain()` clears `pending`, so the
   * very next offer of the sentence that was *just sent* compares against null, is treated as new,
   * and is queued again. The tick offers on every one of its 4 Hz passes, so a reader who is sitting
   * still on one sentence would emit `{t:"at"}` at the full 2/s forever — and §7.1's other half is
   * that the server writes `room_members.ord` for each one, synchronously, with `better-sqlite3`, on
   * the event loop that is streaming audio. Nothing errors. Four idle members become eight writes a
   * second on four aarch64 cores. Caught by `test/room-client.test.ts`, not by looking at it.
   */
  private lastOffered: { sid: string; following: boolean; visibleEndSid?: string } | null = null;

  /**
   * Record a new position. Returns the message to send **now**, or null if it was buffered.
   *
   * A repeat of the same sid *and* the same follow flag is dropped outright: the trigger is a
   * sentence change, and a re-render is not one.
   */
  offer(
    pos: BookPos,
    following: boolean,
    now: number,
    visibleEndSid?: string | null,
  ): { pos: BookPos; following: boolean; visibleEndSid?: string } | null {
    const visible = visibleEndSid || undefined;
    const last = this.lastOffered;
    if (
      last !== null &&
      last.sid === pos.sid &&
      last.following === following &&
      last.visibleEndSid === visible
    ) return null;
    this.lastOffered = { sid: pos.sid, following, ...(visible ? { visibleEndSid: visible } : {}) };
    this.pending = { pos, following, ...(visible ? { visibleEndSid: visible } : {}) };
    return this.drain(now);
  }

  /** Called from the 250 ms tick — the only thing `read` mode's tick does (§7.4). */
  drain(now: number): { pos: BookPos; following: boolean; visibleEndSid?: string } | null {
    if (this.pending === null) return null;
    if (now - this.lastSentAt < AT_MIN_GAP_MS) return null;
    const out = this.pending;
    this.pending = null;
    this.lastSentAt = now;
    return out;
  }

  /**
   * A reconnect re-sends where we are, whatever the gap says: the server lost the last one.
   *
   * `lastOffered` is cleared too, so the next offer of the sentence we are *already* on is not
   * deduplicated away — after a reconnect the server has no idea where we are, and "we already told
   * them" is false.
   */
  reset(): void {
    this.lastSentAt = -Infinity;
    this.lastOffered = null;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * Codes and URLs
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Room codes are upper-case and typed by humans off a phone screen, so the comparison is
 * case-insensitive everywhere and the canonical form is upper.
 *
 * Whitespace is stripped rather than rejected because the overwhelmingly common way a code arrives
 * is a paste out of a message with a trailing space, and refusing that is a refusal a person cannot
 * see the cause of.
 */
export function normalizeCode(code: string): string {
  return code.replace(/\s+/g, "").toUpperCase();
}

/** §7.6's shareable URL. A client route (see vite.config.ts) — `/s/:token` is the server one. */
export const roomPath = (code: string): string => `/r/${encodeURIComponent(normalizeCode(code))}`;

export function roomShareUrl(code: string, origin: string): string {
  return `${origin.replace(/\/+$/, "")}${roomPath(code)}`;
}

export function wsUrl(loc: { protocol: string; host: string }): string {
  return `${loc.protocol === "https:" ? "wss" : "ws"}://${loc.host}/ws`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Room passwords, locally
 * ══════════════════════════════════════════════════════════════════════════ */

const PW_KEY = "ab.roomPw";

type PwBag = Record<string, string>;

function readPwBag(): PwBag {
  try {
    const raw = localStorage.getItem(PW_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as PwBag) : {};
  } catch {
    return {};
  }
}

/**
 * Remember a room's password so a reconnect — which on a phone happens every time the screen unlocks
 * — does not put a password prompt in front of somebody who is already a member.
 *
 * `localStorage` and not a cookie, for the same reason as `deviceId`: it must never travel on a
 * request. §7.1's server-side story is the real defence (salted, `timingSafeEqual`, 6 fails/60 s);
 * this is a convenience on top of it and is treated as untrusted by the server like any other input.
 */
export function getRoomPassword(code: string): string | null {
  return readPwBag()[normalizeCode(code)] ?? null;
}

export function setRoomPassword(code: string, password: string): void {
  const bag = readPwBag();
  const key = normalizeCode(code);
  if (password) bag[key] = password;
  else delete bag[key];
  try {
    localStorage.setItem(PW_KEY, JSON.stringify(bag));
  } catch {
    /* Private browsing. The prompt on reconnect is the cost, and it is a visible one. */
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * The socket
 * ══════════════════════════════════════════════════════════════════════════ */

/** The slice of `WebSocket` this file uses. A real one satisfies it; the test supplies a fake. */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
}

export type TimerToken = unknown;

export interface RoomSocketHost {
  open(url: string): SocketLike;
  now(): number;
  setTimer(fn: () => void, ms: number): TimerToken;
  clearTimer(token: TimerToken): void;
  /** The url to dial. A function rather than a string so a test can move it. */
  url(): string;
  /** `hello.deviceId` — "is this the same phone that was reading last night" (§7.6). */
  device(): string;
}

export const OPEN_STATE = 1;

export interface RoomSocketEvents {
  message(msg: ServerMsg): void;
  /** True on `welcome` (not on `open` — a socket that has not been greeted cannot carry intent). */
  connection(up: boolean): void;
  /**
   * A reconnect completed and the rejoin was sent. §7.5: **on reconnect, `resync()`** — the room
   * moved while an iPhone had the tab suspended, and the correct response is to put ourselves back
   * where everyone is without emitting an intent that would move them.
   */
  rejoined(code: string): void;
}

/**
 * The socket, its rejoin, and its pending queue.
 *
 * Three properties that are easy to lose and expensive to debug:
 *
 * 1. **Nothing is sent before `welcome`.** `helloAcked` gates every write; anything offered earlier
 *    is queued and flushed in order. A `join` racing the `hello` is answered with "who are you".
 * 2. **`rejoinCode` survives the socket.** §7.6: membership is durable and a disconnect does not
 *    remove it. iOS suspends a backgrounded tab's WebSocket within ~30 s of screen lock while the
 *    audio keeps playing, so a room that lost your seat on disconnect would lose it every night.
 * 3. **`wantOpen` is what distinguishes a drop from a departure.** A close with `wantOpen` set
 *    reconnects; `leave()` clears it first. Without the flag, `leave()` reconnects you into the room
 *    you just left, which looks like the Leave button not working.
 */
export class RoomSocket {
  private sock: SocketLike | null = null;
  private helloAcked = false;
  private wantOpen = false;
  private rejoinCode: string | null = null;
  private rejoinPassword: string | null = null;
  private queued: ClientMsg[] = [];
  private attempt = 0;
  private reconnectTimer: TimerToken = null;
  private pingTimer: TimerToken = null;
  private closed = false;
  private name: string | null = null;

  readonly clock = new ClockOffset();

  constructor(
    private readonly host: RoomSocketHost,
    private readonly events: Partial<RoomSocketEvents> = {},
  ) {}

  /** Guests may claim a name; §7.6 says it is ignored for signed-in users and the handle used. */
  setName(name: string | null): void {
    this.name = name;
  }

  get connected(): boolean {
    return this.helloAcked && this.sock !== null && this.sock.readyState === OPEN_STATE;
  }

  get code(): string | null {
    return this.rejoinCode;
  }

  serverNow(): number {
    return this.clock.serverNow(this.host.now());
  }

  connect(): void {
    if (this.closed) return;
    this.wantOpen = true;
    if (this.sock !== null && this.sock.readyState <= OPEN_STATE) return;
    this.dial();
  }

  private dial(): void {
    const s = this.host.open(this.host.url());
    this.sock = s;
    this.helloAcked = false;

    s.onopen = () => {
      const hello: ClientMsg = this.name
        ? { t: "hello", deviceId: this.host.device(), name: this.name }
        : { t: "hello", deviceId: this.host.device() };
      // Straight to the wire, not through `send()`: `send()` queues anything before `welcome`, and
      // `hello` is the message that produces `welcome`.
      s.send(JSON.stringify(hello));
    };

    s.onmessage = (ev) => {
      const msg = parseServerMsg(ev.data);
      if (msg === null) return;
      this.handle(msg);
      this.events.message?.(msg);
    };

    s.onclose = () => {
      this.helloAcked = false;
      if (this.sock === s) this.sock = null;
      this.stopPing();
      this.events.connection?.(false);
      if (!this.wantOpen || this.closed) return;
      const wait = RECONNECT_BACKOFF_MS[Math.min(this.attempt, RECONNECT_BACKOFF_MS.length - 1)] ?? 10_000;
      this.attempt++;
      this.reconnectTimer = this.host.setTimer(() => {
        this.reconnectTimer = null;
        if (this.wantOpen && !this.closed) this.dial();
      }, wait);
    };

    s.onerror = () => {
      /* `close` always follows; reconnecting from both would dial twice. */
    };
  }

  private handle(msg: ServerMsg): void {
    switch (msg.t) {
      case "welcome": {
        this.helloAcked = true;
        this.attempt = 0;
        this.clock.seed(msg.serverTime, this.host.now());
        const code = this.rejoinCode;
        if (code !== null) {
          this.raw({ t: "join", code, password: this.rejoinPassword ?? getRoomPassword(code) ?? undefined });
        }
        const q = this.queued;
        this.queued = [];
        for (const m of q) this.raw(m);
        this.raw({ t: "ping", clientTime: this.host.now() });
        this.startPing();
        this.events.connection?.(true);
        if (code !== null) this.events.rejoined?.(code);
        break;
      }
      case "joined":
        this.rejoinCode = normalizeCode(msg.code);
        break;
      case "room":
        this.clock.seed(msg.room.serverTime, this.host.now());
        break;
      case "sync":
        this.clock.seed(msg.serverTime, this.host.now());
        break;
      case "pong":
        this.clock.sample(msg.clientTime, msg.serverTime, this.host.now());
        break;
      case "ended":
        // The room is gone. Reconnecting would dial a room that no longer exists, forever.
        this.wantOpen = false;
        this.rejoinCode = null;
        this.hangUp();
        break;
      default:
        break;
    }
  }

  /** Queue-aware send. Everything the app calls goes through here. */
  send(msg: ClientMsg): void {
    if (this.closed) return;
    if (this.connected) this.raw(msg);
    else {
      // Bounded: a tab left open on a dead network for an hour must not accumulate a megabyte of
      // presence updates that all land at once on reconnect and each move somebody's dot.
      if (this.queued.length >= 32) this.queued.shift();
      this.queued.push(msg);
    }
  }

  private raw(msg: ClientMsg): void {
    try {
      this.sock?.send(JSON.stringify(msg));
    } catch {
      /* A socket closing mid-send is a disconnect, and `onclose` is already the handler for it. */
    }
  }

  create(opts: Omit<Extract<ClientMsg, { t: "create" }>, "t">): void {
    this.wantOpen = true;
    if (opts.password) this.rejoinPassword = opts.password;
    this.connect();
    this.send({ t: "create", ...opts });
  }

  join(code: string, password?: string): void {
    const c = normalizeCode(code);
    this.rejoinCode = c;
    this.rejoinPassword = password ?? getRoomPassword(c);
    if (password) setRoomPassword(c, password);
    this.wantOpen = true;
    this.connect();
    this.send({ t: "join", code: c, password: this.rejoinPassword ?? undefined });
  }

  /** Deliberate departure. Clears `wantOpen` FIRST so `onclose` does not reconnect us back in. */
  leave(): void {
    this.wantOpen = false;
    if (this.connected) this.raw({ t: "leave" });
    this.rejoinCode = null;
    this.rejoinPassword = null;
    this.queued = [];
    this.hangUp();
  }

  ping(): void {
    if (this.connected) this.raw({ t: "ping", clientTime: this.host.now() });
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = this.host.setTimer(() => {
      this.pingTimer = null;
      if (this.closed) return;
      this.ping();
      this.startPing();
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      this.host.clearTimer(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private hangUp(): void {
    this.stopPing();
    if (this.reconnectTimer !== null) {
      this.host.clearTimer(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const s = this.sock;
    this.sock = null;
    this.helloAcked = false;
    try {
      s?.close();
    } catch {
      /* already gone */
    }
  }

  /** Unmount. Idempotent, and after it nothing this object owns can fire again. */
  destroy(): void {
    this.closed = true;
    this.wantOpen = false;
    this.hangUp();
  }
}

/**
 * Parse a frame. A malformed one is dropped, never guessed at.
 *
 * The `t` check is not ceremony: `switch (msg.t)` over an object with no `t` falls through every
 * case silently, and a socket that has started answering with HTML — which is what a proxy
 * misconfiguration produces, and vite.config.ts has a whole comment about one — would otherwise show
 * up as a room that simply never updates.
 */
export function parseServerMsg(data: unknown): ServerMsg | null {
  if (typeof data !== "string") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const t = (parsed as { t?: unknown }).t;
  if (typeof t !== "string") return null;
  return parsed as ServerMsg;
}

/** The browser host. `null` where there is no `WebSocket` — i.e. `node --test`. */
export function browserRoomHost(): RoomSocketHost | null {
  if (typeof WebSocket === "undefined" || typeof location === "undefined") return null;
  return {
    open: (url) => new WebSocket(url) as unknown as SocketLike,
    now: () => Date.now(),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (token) => clearTimeout(token as ReturnType<typeof setTimeout>),
    url: () => wsUrl(location),
    device: () => deviceId(),
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * `GET /api/v1/rooms/mine` (§7.6)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * A row on the "Reading together" shelf strip.
 *
 * `myPos`/`roomPos` are both carried and are deliberately not merged: §7.6's *"the room bookmark and
 * your personal bookmark are different rows and the app asks once"* is the whole point, and a list
 * endpoint that collapsed them would make the question unaskable from the shelf.
 */
export interface MyRoom {
  code: string;
  name: string;
  bookId: string;
  bookTitle: string;
  coverSha: string | null;
  mode: PlaybackMode;
  memberCount: number;
  connectedCount: number;
  persistent: boolean;
  lastActivityAt: number;
  /** Where the room stopped. Null for a room that has never played. */
  roomPos: BookPos | null;
  roomChapterOrd: number | null;
  roomChapterTitle: string | null;
}

export interface MyRoomsResponse {
  rooms: MyRoom[];
}

/**
 * Read a `/rooms/mine` payload defensively about the **envelope** and strictly about the
 * **contents** — the house rule from `lib/position.ts` and `lib/api.ts`. A row without a `code` is
 * dropped rather than repaired: a "Reading together" card that navigates to `/r/undefined` is worse
 * than a card that is not there.
 */
export function asMyRooms(body: unknown): MyRoom[] {
  const list = Array.isArray(body)
    ? body
    : body && typeof body === "object" && Array.isArray((body as { rooms?: unknown }).rooms)
      ? (body as { rooms: unknown[] }).rooms
      : [];
  const out: MyRoom[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Partial<MyRoom>;
    if (typeof r.code !== "string" || r.code.length === 0) continue;
    if (typeof r.bookId !== "string") continue;
    out.push({
      code: normalizeCode(r.code),
      name: typeof r.name === "string" ? r.name : "Reading room",
      bookId: r.bookId,
      bookTitle: typeof r.bookTitle === "string" ? r.bookTitle : "",
      coverSha: typeof r.coverSha === "string" ? r.coverSha : null,
      mode: r.mode === "read" ? "read" : "listen",
      memberCount: numberOr(r.memberCount, 0),
      connectedCount: numberOr(r.connectedCount, 0),
      persistent: r.persistent !== false,
      lastActivityAt: numberOr(r.lastActivityAt, 0),
      roomPos: isBookPos(r.roomPos) ? r.roomPos : null,
      roomChapterOrd: typeof r.roomChapterOrd === "number" ? r.roomChapterOrd : null,
      roomChapterTitle: typeof r.roomChapterTitle === "string" ? r.roomChapterTitle : null,
    });
  }
  return out;
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/**
 * LAW 9, absent ≠ zero. A `BookPos` with a missing `frac` is not a `BookPos` with `frac: 0` — it is
 * a position we were not told, and coercing it lands a resume at the head of a sentence somebody was
 * halfway through. Every field is required and a partial object is refused whole.
 */
export function isBookPos(v: unknown): v is BookPos {
  if (!v || typeof v !== "object") return false;
  const p = v as Partial<BookPos>;
  return (
    typeof p.sid === "string" &&
    p.sid.length > 0 &&
    typeof p.ord === "number" &&
    Number.isFinite(p.ord) &&
    typeof p.ms === "number" &&
    Number.isFinite(p.ms) &&
    typeof p.frac === "number" &&
    Number.isFinite(p.frac)
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Who is where
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * §7.6: *"Karti is 3 pages ahead"* — **from `ord`, not `ms`**.
 *
 * `ms` moves whenever a page is narrated (§4.6) and two members can legitimately hold different
 * `timelineVersion`s, so a difference of milliseconds is a difference of *timelines* as often as it
 * is a difference of position. `ord` cannot move, because editions are immutable (LAW 1).
 *
 * The page here is the **canonical** page (`PAGE_TARGET_CHARS = 1500`, §2.3), not the display column,
 * because a display page is a property of one device's font size and the sentence *"Karti is 3 pages
 * ahead"* has to mean the same thing on the phone and on the laptop reading it. `sentencesPerPage`
 * comes from the book (`sentenceCount / pageCount`), so a book of long sentences is not reported as
 * twenty pages ahead when it is three.
 *
 * ⚠️ **Returns null when the two ordinals are not comparable in MAGNITUDE**, and that is the trap
 * `lib/position.ts` names by phase number: *"a server that starts writing `positions.ord` itself (the
 * co-read tick, P5) and a client that is still deriving would disagree with no error anywhere."*
 *
 * `derivedOrd()` (`chapterOrd·10⁷ + blockOrd·10³ + sentOrd`) induces exactly the same **total order**
 * as the server's dense ordinal, which is all `comparePos` needs — so a derived ord is a perfectly
 * good thing to *sort* by and to send. It preserves no **magnitude** at all: one chapter apart is a
 * difference of ten million. Divided by `sentencesPerPage` that is *"Karti is 6,666 pages ahead"*,
 * which is not a number anybody reads twice — but subtract two derived ords in the same block and
 * you get 1, which reads as perfectly sensible and is equally meaningless. The second one is the
 * dangerous one, so the caller passes `sentencesPerPage <= 0` when the index is not exact and this
 * refuses to answer rather than answering plausibly.
 */
export function pagesAhead(theirs: BookPos, mine: BookPos, sentencesPerPage: number): number | null {
  if (!(sentencesPerPage > 0)) return null;
  const delta = theirs.ord - mine.ord;
  // Truncate toward zero, not round: half a page ahead is "on your page", and rounding 0.5 up puts
  // "1 page ahead" on the screen of somebody looking at the same paragraph.
  const n = Math.trunc(delta / sentencesPerPage);
  // `Math.trunc` of a small negative is `-0`, which templates as "0" and compares equal to 0 — so it
  // is invisible everywhere except in a test using `Object.is`. Normalised here so callers never have
  // to know, rather than left for one of them to discover.
  return n === 0 ? 0 : n;
}

/** A member's initials, for the margin dot. One letter for one word, two for more. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = [...(parts[0] ?? "")][0] ?? "?";
  if (parts.length === 1) return first.toUpperCase();
  const last = [...(parts[parts.length - 1] ?? "")][0] ?? "";
  return (first + last).toUpperCase();
}

/**
 * `canControl` from `shared/protocol.ts`, made null-tolerant for a screen that has not joined yet.
 *
 * ⚠️ **The predicate itself is NOT reimplemented here**, and the null handling is the only thing this
 * adds. LAW 13's shape, one level down: the client calls the same function the server enforces with,
 * so the greyed-out control and the refusal agree by construction. A local `switch` over
 * `controlMode` is precisely what this repo means by "two implementations of one rule diverge on the
 * case nobody screenshotted" — and it is not hypothetical here. This function *was* such a switch for
 * about an hour, written from §7.2's prose against a guessed `"everyone" | "hosts" | "owner"`, and
 * `shared/protocol.ts` says `"everyone" | "host"`. It would have refused the remote to every host in
 * every host-controlled room, silently, and the greyed button would have looked deliberate.
 */
export function canControlRoom(room: RoomSnapshot | null, key: string | null): boolean {
  if (room === null || key === null) return false;
  return protocolCanControl(room, key);
}

export const memberOf = (room: RoomSnapshot | null, key: string | null): RoomMember | null =>
  room === null || key === null ? null : (room.members.find((m) => m.key === key) ?? null);
